import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { corporateSubmitValidationSchema } from "validations/login.validation";

import { useAppDispatch, useAppSelector } from "redux/hooks";
import { ILoginRequest } from "models/Login/login.model";
import { loginAsync } from "redux/actions/Login.action";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import InformationImageList from "./InformationImageList";
import {
  Panel,
  PanelHeader,
  Flex,
  TextField,
  Button,
  Typography,
} from "components";
import { defaultFormValuesCorp } from "./login.constant";
import { ResetLoginError } from "redux/slices/LoginSlice";
import { webSecurityAsync } from "redux/actions/WebSecurity.action";

const CorporateLogin: FC = () => {
  const [captchaToogle, setCaptchaToogle] = useState<boolean>(false);

  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { error, success, user } = useAppSelector((state) => state.login);

  const { control, handleSubmit, errors, reset } = useForm<ILoginRequest>({
    defaultValues: defaultFormValuesCorp,
    resolver: yupResolver(corporateSubmitValidationSchema(t)),
  });

  useEffect(() => {
    if (success) {
      navigate("/otp", {
        state: {
          fromLogin: true,
        },
      });
    } else if (user?.isUserNameRequired) {
      setCaptchaToogle(true);
    }
  }, [navigate, success, error]);

  const formSubmit = async (data: ILoginRequest): Promise<void> => {
    dispatch(ResetLoginError());
    data.corporateIndividual = "1";
    dispatch(loginAsync(data));
    reset();
  };

  const handleMbbchange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(webSecurityAsync());
  };

  return (
    <>
      <Panel width='646px' marginTop='0'>
        <Flex.ElementWrapper
          height={24}
          justifyContent='center'
          margin='20px 0 10px 0'
        ></Flex.ElementWrapper>
        <PanelHeader title={t("merchant_login")} />
        <form onSubmit={handleSubmit(formSubmit)} noValidate>
          <Flex.StyledFormWrapper>
            <Flex.Row>
              <Flex.Col>
                <TextField
                  control={control}
                  error={errors.merchantNumber?.message}
                  label={t("mbb_number_corporate").toString()}
                  maxLength={15}
                  name='merchantNumber'
                  type='numeric'
                  onBlur={handleMbbchange}
                />
              </Flex.Col>
            </Flex.Row>

            <Flex.Row>
              <Flex.Col>
                <TextField
                  control={control}
                  error={errors.userId?.message}
                  label={t("user_code_corporate").toString()}
                  maxLength={10}
                  name='userId'
                  type='numeric'
                />
              </Flex.Col>
            </Flex.Row>

            <Flex.Row>
              <Flex.Col>
                <TextField
                  control={control}
                  error={errors.password?.message}
                  label={t("password").toString()}
                  maxLength={6}
                  name='password'
                  type='masked'
                />
              </Flex.Col>
            </Flex.Row>

            {captchaToogle && (
              <>
                <Flex.Row>
                  <Flex.Col>
                    <TextField
                      control={control}
                      error={errors.adSoyad?.message}
                      label={`
                        ${t("name").toString()} ${t("surname").toString()}
                      `}
                      name='adSoyad'
                      type='alphabetic'
                    />
                  </Flex.Col>
                </Flex.Row>
              </>
            )}
          </Flex.StyledFormWrapper>

          <Flex.Row
            backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
            justifyContent='flex-end'
            isResponsive
            noMargin
          >
            <Flex.Col offSet={22} size={0.5} maxWidth='130px'>
              <Button text={t("login_button").toString()} type='submit' />
            </Flex.Col>
          </Flex.Row>
        </form>
        <Flex.Seperator marginTop={isMobile ? 32 : 59} />
        <Flex.Row
          backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
          justifyContent={isMobile ? "center" : "flex-start"}
          noMargin
        >
          <Typography
            color={DIGIPOSTHEME.colors.secondaryGrayPrimary}
            letterSpacing='1px'
            size='12px'
            transform='none'
            variant='span'
            weight='500'
            margin='0 20px'
          >
            {t("login_cards_title")}
          </Typography>
        </Flex.Row>

        <Flex.Seperator marginTop={11} />

        <Flex.Row
          backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
          noMargin
          justifyContent={isMobile ? "center" : "space-between"}
        >
          <InformationImageList />
        </Flex.Row>
      </Panel>
    </>
  );
};

export default CorporateLogin;
